import { CommonModule, formatNumber } from '@angular/common';
import { Component, OnInit, Input, PipeTransform, Pipe } from '@angular/core';
import {
  LeaderboardV2Query,
  LeaderboardV2Service,
  MonthlyLeaderboardEntry,
  LeaderboardV2Entry,
  LeaderboardType,
  Sort,
} from '@fgb/core';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { OwlCarouselSettings, sortMultiple } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { firstValueFrom } from 'rxjs';
import { TabComponent } from 'src/app/shared/components/nav-tab/tab.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { Populations } from 'src/app/shared/utilities/populations';

@Pipe({ name: 'FortressCurrencyPipe', standalone: true })
export class CustomPipe implements PipeTransform {
  transform(value: any) {
    return (value = formatNumber(value, 'en-GB', '1.0-0'));
  }
}

interface LeaderboardV2EntryWithTier extends LeaderboardV2Entry {
  tier?: string;
}

@Component({
  selector: 'fgb-leaderboard',
  standalone: true,
  imports: [TabComponent, FGBSharedModule, CommonModule, PipesModule, CarouselModule, SharedModule, CustomPipe],
  templateUrl: './leaderboard.component.html',
  styleUrls: ['./leaderboard.component.scss'],
})
export class LeaderboardComponent implements OnInit {
  @Input() toShow: number;
  @Input() LeaderboardType: LeaderboardType;
  @Input() isWidget: boolean = false;
  type = LeaderboardType;

  yearlyLeaderboards: LeaderboardV2Entry[] | undefined;
  monthlyV2: MonthlyLeaderboardEntry;
  monthlyLeaderboard: any[] = [];
  showMonthly: boolean = true;
  loadingMonth: number | null = null;
  loadingYear: boolean = true;
  currentUser: LeaderboardV2Entry | undefined;
  topThree: LeaderboardV2EntryWithTier[] = [];
  challengers: LeaderboardV2Entry[] = [];
  customOptions: OwlOptions = {
    items: 1,
    margin: OwlCarouselSettings.Margin,
    mouseDrag: false,
    touchDrag: false,
    pullDrag: false,
    navSpeed: 700,
    nav: true,
    dots: false,
    startPosition: this.monthlyLeaderboard.length - 1,
    navText: ['<i class="material-icons fw-bold">chevron_left</i>', '<i class="material-icons fw-bold">chevron_right</i>'],
    stagePadding: 1,
    autoHeight: true,
  };
  isUserTopThree: boolean = false;
  populations = Populations;

  constructor(private leaderboardV2Query: LeaderboardV2Query, private leaderboardV2Service: LeaderboardV2Service) {}

  ngOnInit() {
    this.initializeMonthlyLeaderboard();
  }

  initializeLeaderboard() {
    this.showMonthly = false;
    if (!this.yearlyLeaderboards) {
      this.loadingYear = true;
    } else {
      this.loadingYear = false;
    }
    this.currentUser = undefined;
    firstValueFrom(this.leaderboardV2Service.fetchLeaderboardV2(this.toShow, this.LeaderboardType))
      .then(() => {
        this.currentUser = this.leaderboardV2Query.getLeaderboardV2().find((entry) => entry.IsCurrentUser);
        this.yearlyLeaderboards = this.leaderboardV2Query
          .getLeaderboardV2()
          .sort(sortMultiple(Sort.by('Position'), Sort.by('Points', 'desc'), Sort.by('FullName')))
          .filter((entry: LeaderboardV2Entry, index: number) => {
            if (this.isWidget) {
              return index < 3; // Show only top 3 entries for widget
            }
            return entry;
          });
        this.challengers = this.yearlyLeaderboards.slice(3, this.yearlyLeaderboards.length);
        this.topThree = this.getTopThreeTiers(this.yearlyLeaderboards);
        this.isUserTopThree = this.topThree.findIndex((entry: LeaderboardV2Entry) => entry.IsCurrentUser) !== -1;
      })
      .finally(() => (this.loadingYear = false));
  }

  async initializeMonthlyLeaderboard() {
    this.showMonthly = true;
    await firstValueFrom(this.leaderboardV2Service.fetchMonthlyLeaderboardV2(this.toShow, this.LeaderboardType));
    this.monthlyV2 = this.leaderboardV2Query.getMonthlyLeaderboardV2();

    // Initialize months array
    this.monthlyLeaderboard = this.monthlyV2.Months.map((dateStr: string) => {
      const date = new Date(dateStr);
      return {
        month: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
        date: date,
        loaded: false,
        entries: [],
        topThree: [],
        challengers: [],
      };
    }).filter((monthData: any) => {
      // Filter out future months
      return monthData.date <= new Date();
    });

    if (this.monthlyLeaderboard.length > 0) {
      await this.loadMonthData(this.monthlyLeaderboard.length - 1);
    }
  }

  // Load data for specific month
  async loadMonthData(index: number) {
    if (index < 0 || index >= this.monthlyLeaderboard.length) return;

    this.loadingMonth = index;
    if (!this.monthlyLeaderboard[index].loaded) {
      this.monthlyLeaderboard[index].loaded = false;
    }
    const dateStr = this.monthlyV2.Months[index];
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    this.currentUser = undefined;
    await firstValueFrom(this.leaderboardV2Service.fetchEachMonthLeaderboardV2(this.toShow, month, year, this.LeaderboardType))
      .then(() => {
        const monthData = this.leaderboardV2Query
          .getEachMonthLeaderboardV2()
          .sort(sortMultiple(Sort.by('Position'), Sort.by('Points', 'desc'), Sort.by('FullName')));
        this.monthlyLeaderboard[index].entries = monthData.filter((entry: LeaderboardV2Entry, index: number) => {
          if (this.isWidget) {
            return index < 3; // Show only top 3 entries for widget
          }
          return entry;
        });
        this.monthlyLeaderboard[index].loaded = true;
        this.monthlyLeaderboard[index].topThree = this.getTopThreeTiers(this.monthlyLeaderboard[index].entries);
        this.monthlyLeaderboard[index].challengers = monthData.slice(3, this.monthlyLeaderboard[index].entries.length);
        this.isUserTopThree =
          this.monthlyLeaderboard[index].topThree.findIndex((entry: LeaderboardV2Entry) => entry.IsCurrentUser) !== -1;
        this.monthlyLeaderboard[index].currentUser = !this.isUserTopThree
          ? monthData.find((entry) => entry.IsCurrentUser)
          : undefined;
      })
      .finally(() => (this.monthlyLeaderboard[index].loaded = true));
  }

  // Carousel event handler
  async onSlideChanged(event: any) {
    const currentIndex = event.startPosition;
    await this.loadMonthData(currentIndex);
  }

  private getTopThreeTiers(rankings: LeaderboardV2Entry[]): LeaderboardV2EntryWithTier[] {
    let tiers = ['gold', 'silver', 'bronze'];
    let position: number = -1;
    return rankings.slice(0, 3).map((entry: LeaderboardV2Entry) => {
      if (entry.Position !== position && position !== -1) {
        tiers.shift();
      }

      position = entry.Position;
      return {
        ...entry,
        tier: tiers[0],
      };
    });
  }

  onTabChage(tab: number): void {
    if (tab === 0) {
      this.initializeMonthlyLeaderboard();
    } else if (tab === 1) {
      this.initializeLeaderboard();
    }
  }
}
